import { AppLayout } from "@/components/layout";
import { apiFetch } from "@/lib/api";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  LineChart, Line, ReferenceLine,
} from "recharts";
import { format, parseISO } from "date-fns";
import { ShieldAlert, ShieldCheck, Activity, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

type IpBlockStat = {
  id: string;
  windowStart: string;
  blockedCount: number;
  totalExpiredCount: number;
  createdAt: string;
  isSpike: boolean;
};

type SecurityStats = {
  stats: IpBlockStat[];
  totalBlocked: number;
  totalCleaned: number;
  spikeCount: number;
  avgBlockedPerWindow: number;
};

export default function AdminSecurity() {
  const [data, setData] = useState<SecurityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [range, setRange] = useState<"7d" | "30d">("7d");

  useEffect(() => {
    setLoading(true);
    setError(false);
    apiFetch<SecurityStats>(`/admin/security/ip-block-stats?range=${range}`)
      .then((d) => { setData(d); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [range]);

  const chartData = (data?.stats ?? []).map((s) => ({
    label: format(parseISO(s.windowStart), range === "7d" ? "EEE ha" : "MMM d"),
    blocked: s.blockedCount,
    cleaned: s.totalExpiredCount,
    isSpike: s.isSpike,
  }));

  const avgBlocked = data?.avgBlockedPerWindow ?? 0;
  const spikeThreshold = avgBlocked * 2;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Login Security</h1>
            <p className="text-muted-foreground text-lg">IP block and cleanup history over time.</p>
          </div>
          <div className="flex items-center gap-2">
            {(["7d", "30d"] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-mono font-semibold transition-colors",
                  range === r
                    ? "bg-primary text-primary-foreground"
                    : "bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white"
                )}
              >
                {r === "7d" ? "Last 7 days" : "Last 30 days"}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <Card className="glass-panel border-red-500/40 bg-red-950/20">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
                <p className="text-sm text-red-300">
                  Failed to load security data. Please refresh the page or try again.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <ShieldAlert className="w-16 h-16 text-red-400" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">IPs Blocked</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {loading ? "..." : (data?.totalBlocked ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Rate-limited logins</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <ShieldCheck className="w-16 h-16 text-emerald-400" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Records Cleaned</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {loading ? "..." : (data?.totalCleaned ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Expired entries removed</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Activity className="w-16 h-16 text-primary" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Avg Blocked / Window</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {loading ? "..." : avgBlocked.toFixed(1)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Per cleanup cycle</p>
            </CardContent>
          </Card>

          <Card className={cn(
            "glass-panel relative overflow-hidden",
            (data?.spikeCount ?? 0) > 0 ? "border-red-500/40" : "border-white/5"
          )}>
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <AlertTriangle className="w-16 h-16 text-amber-400" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Spike Windows</CardDescription>
              <CardTitle className={cn(
                "text-3xl font-mono",
                (data?.spikeCount ?? 0) > 0 ? "text-red-400" : "text-white"
              )}>
                {loading ? "..." : (data?.spikeCount ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {(data?.spikeCount ?? 0) > 0 ? "Unusual bursts detected" : "No unusual activity"}
              </p>
            </CardContent>
          </Card>
        </div>

        {(data?.spikeCount ?? 0) > 0 && !loading && (
          <Card className="glass-panel border-red-500/40 bg-red-950/20">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
                <p className="text-sm text-red-300">
                  <span className="font-semibold">{data?.spikeCount} spike{(data?.spikeCount ?? 0) !== 1 ? "s" : ""} detected</span> in this period — windows where blocked IPs were more than 2× the average ({avgBlocked.toFixed(1)}). This may indicate a coordinated login attack.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Blocked IPs Over Time</CardTitle>
            <CardDescription className="text-muted-foreground">
              Number of IPs that hit the login rate limit per cleanup window.
              {spikeThreshold > 0 && (
                <> The dashed line marks 2× the average ({spikeThreshold.toFixed(0)}).</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] w-full">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                  <ShieldCheck className="w-10 h-10 opacity-40" />
                  <p className="text-sm">No data yet for this period.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                    <XAxis dataKey="label" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <RechartsTooltip
                      cursor={{ fill: "#1e1e24" }}
                      contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                      itemStyle={{ color: "#f87171" }}
                      formatter={(val: number) => [val, "Blocked IPs"]}
                    />
                    {spikeThreshold > 0 && (
                      <ReferenceLine y={spikeThreshold} stroke="#f87171" strokeDasharray="4 4" strokeOpacity={0.6} />
                    )}
                    <Bar
                      dataKey="blocked"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                      fill="#f87171"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Records Cleaned Up Over Time</CardTitle>
            <CardDescription className="text-muted-foreground">
              Total expired login rate-limit records removed per cleanup cycle.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[220px] w-full">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                  <Activity className="w-10 h-10 opacity-40" />
                  <p className="text-sm">No data yet for this period.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                    <XAxis dataKey="label" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <RechartsTooltip
                      cursor={{ stroke: "#27272e" }}
                      contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                      itemStyle={{ color: "#e8c848" }}
                      formatter={(val: number) => [val, "Records Cleaned"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="cleaned"
                      stroke="#e8c848"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#e8c848" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {!loading && (data?.stats ?? []).length > 0 && (
          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Cleanup Log</CardTitle>
              <CardDescription className="text-muted-foreground">Recent cleanup cycles, newest first.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-muted-foreground text-xs uppercase tracking-wider">
                      <th className="text-left py-2 pr-6 font-semibold">Window</th>
                      <th className="text-right py-2 pr-6 font-semibold">Blocked IPs</th>
                      <th className="text-right py-2 pr-6 font-semibold">Total Cleaned</th>
                      <th className="text-right py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {[...(data?.stats ?? [])].reverse().slice(0, 20).map((s) => (
                      <tr key={s.id} className={cn("transition-colors", s.isSpike && "bg-red-950/20")}>
                        <td className="py-3 pr-6 text-white font-mono text-xs">
                          {format(parseISO(s.windowStart), "MMM d, yyyy HH:mm")}
                        </td>
                        <td className={cn("py-3 pr-6 text-right font-mono", s.blockedCount > 0 ? "text-red-400" : "text-muted-foreground")}>
                          {s.blockedCount}
                        </td>
                        <td className="py-3 pr-6 text-right font-mono text-muted-foreground">
                          {s.totalExpiredCount}
                        </td>
                        <td className="py-3 text-right">
                          {s.isSpike ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-950/40 px-2 py-0.5 rounded-full">
                              <AlertTriangle className="w-3 h-3" /> Spike
                            </span>
                          ) : s.blockedCount === 0 ? (
                            <span className="text-xs text-muted-foreground">Clean</span>
                          ) : (
                            <span className="text-xs text-amber-400">Activity</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
